import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsTrigger } from '../ui/tabs';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Settings,
  Plus,
  Edit,
  Trash2,
  Code,
  Users,
  Globe,
  GraduationCap,
  Calendar,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import { getJobParameters, setJobParameters, fetchJobParameters } from '../../services/jobParametersService';
import { apiClient } from '@/lib/api-client';
import { useConfirmDialog } from '../ui/confirm-dialog';

interface JobParameterManagementProps {
  jobs?: any[];
}

export function JobParameterManagement({ jobs = [] }: JobParameterManagementProps) {
  const [activeTab, setActiveTab] = useState('skills');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  // Centralized parameters loaded from service
  const [parameters, setParameters] = useState(getJobParameters());
  const [loading, setLoading] = useState(false);

  // Load from backend on mount
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const synced = await fetchJobParameters();
        setParameters(synced);
        setJobParameters(synced);
      } catch (err) {
        console.error('Failed to sync job parameters:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    category: '',
    level: '',
    range: ''
  });

  const parameterConfigs = {
    skills: {
      title: 'Skills Management',
      description: 'Manage skill requirements and categories',
      icon: Code,
      fields: ['name', 'category'],
      columns: ['Skill', 'Category', 'Usage', 'Status', 'Actions']
    },
    employeeTypes: {
      title: 'Employee Types',
      description: 'Manage employment types and classifications',
      icon: Users,
      fields: ['name', 'description'],
      columns: ['Type', 'Description', 'Usage', 'Status', 'Actions']
    },
    languages: {
      title: 'Languages',
      description: 'Manage language requirements',
      icon: Globe,
      fields: ['name', 'level'],
      columns: ['Language', 'Level', 'Usage', 'Status', 'Actions']
    },
    jobLevels: {
      title: 'Job Levels',
      description: 'Manage job hierarchy levels',
      icon: GraduationCap,
      fields: ['name', 'description'],
      columns: ['Level', 'Description', 'Usage', 'Status', 'Actions']
    },
    experience: {
      title: 'Experience Ranges',
      description: 'Manage experience requirements',
      icon: Calendar,
      fields: ['name', 'range'],
      columns: ['Experience', 'Range', 'Usage', 'Status', 'Actions']
    }
  };

  const currentConfig = parameterConfigs[activeTab as keyof typeof parameterConfigs];

  // Compute real usage from jobs for display, without persisting usage
  const displayParameters = useMemo(() => {
    const safeJobs = Array.isArray(jobs) ? jobs : [];
    const normalize = (str: any) => String(str || '')
      .toLowerCase()
      .replace(/[_-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const textIncludes = (text: any, token: string) => {
      const t = String(text || '').toLowerCase();
      return t.includes(token.toLowerCase());
    };

    // helpers to count usage per item list
    const skillsWithUsage = parameters.skills.map(item => {
      const key = normalize(item.name);
      const count = safeJobs.reduce((acc, job) => {
        const jobSkills: string[] = Array.isArray(job?.skills) ? job.skills : [];
        const reqText = Array.isArray(job?.requirements) ? job.requirements.join(' ') : String(job?.requirements || '');
        const title = job?.title || '';
        const hasSkill = jobSkills.map(normalize).includes(key)
          || textIncludes(reqText, item.name)
          || textIncludes(title, item.name);
        return acc + (hasSkill ? 1 : 0);
      }, 0);
      return { ...item, usage: count };
    });

    const typeNameToCode: Record<string, string> = {
      'full time': 'full_time',
      'part time': 'part_time',
      'contract': 'contract',
      'internship': 'internship',
      'freelance': 'freelance',
      'remote': 'remote',
    };
    const employeeTypesWithUsage = parameters.employeeTypes.map(item => {
      const nameNorm = normalize(item.name);
      const code = typeNameToCode[nameNorm] || nameNorm.replace(' ', '_');
      const count = safeJobs.reduce((acc, job) => {
        const jType = job?.employment_type || job?.type || '';
        const jTypeNorm = normalize(jType).replace(' ', '_');
        const match = jTypeNorm === code || normalize(jType) === nameNorm;
        return acc + (match ? 1 : 0);
      }, 0);
      return { ...item, usage: count };
    });

    const languagesWithUsage = parameters.languages.map(item => {
      const nameNorm = normalize(item.name);
      const count = safeJobs.reduce((acc, job) => {
        const langs: string[] = Array.isArray(job?.languages) ? job.languages : [];
        const reqText = Array.isArray(job?.requirements) ? job.requirements.join(' ') : String(job?.requirements || '');
        const match = langs.map(normalize).includes(nameNorm) || textIncludes(reqText, item.name);
        return acc + (match ? 1 : 0);
      }, 0);
      return { ...item, usage: count };
    });

    const levelNameToCode: Record<string, string> = {
      'entry level': 'entry',
      'mid level': 'mid',
      'senior level': 'senior',
      'executive level': 'executive',
      'executive': 'executive',
      'internship': 'internship',
    };
    const jobLevelsWithUsage = parameters.jobLevels.map(item => {
      const nameNorm = normalize(item.name);
      const code = levelNameToCode[nameNorm] || nameNorm;
      const count = safeJobs.reduce((acc, job) => {
        const jLevel = job?.experience_level || job?.jobLevel || '';
        const jLevelNorm = normalize(jLevel);
        const match = jLevelNorm === code || jLevelNorm === nameNorm || textIncludes(job?.requirements, item.name);
        return acc + (match ? 1 : 0);
      }, 0);
      return { ...item, usage: count };
    });

    const experienceWithUsage = parameters.experience.map(item => {
      const rangeNorm = normalize(item.range || item.name);
      const rgx = new RegExp(item.range?.replace('+', '\\+') || item.name, 'i');
      const count = safeJobs.reduce((acc, job) => {
        const reqText = Array.isArray(job?.requirements) ? job.requirements.join(' ') : String(job?.requirements || '');
        const matchRange = rgx.test(reqText);
        // Also try simple number-year patterns
        const simpleMatch = /\b(\d+\s*-\s*\d+|\d+\s*\+?)\s*years\b/i.test(reqText) && rgx.test(reqText);
        const matchName = normalize(job?.experience) === rangeNorm;
        return acc + ((matchRange || simpleMatch || matchName) ? 1 : 0);
      }, 0);
      return { ...item, usage: count };
    });

    return {
      skills: skillsWithUsage,
      employeeTypes: employeeTypesWithUsage,
      languages: languagesWithUsage,
      jobLevels: jobLevelsWithUsage,
      experience: experienceWithUsage,
    } as typeof parameters;
  }, [jobs, parameters]);

  const currentData = displayParameters[activeTab as keyof typeof parameters];

  const filteredData = currentData.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ((item as any).category && (item as any).category.toLowerCase().includes(searchTerm.toLowerCase())) ||
    ((item as any).description && (item as any).description.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  const handleAdd = async () => {
    if (!newItem.name) {
      toast.error('Name is required');
      return;
    }

    try {
      const res = await apiClient.createJobParameter(activeTab, newItem);
      const item = {
        ...res.item,
        id: res.item._id || res.item.id || Date.now().toString(),
        usage: 0
      };

      const updated = {
        ...parameters,
        [activeTab]: [...parameters[activeTab as keyof typeof parameters], item]
      } as any;
      setParameters(updated);
      setJobParameters(updated);

      setNewItem({
        name: '',
        description: '',
        category: '',
        level: '',
        range: ''
      });
      setIsAddDialogOpen(false);
      toast.success(`${currentConfig.title} item added successfully!`);
    } catch (e: any) {
      toast.error(e.message || 'Failed to add item');
    }
  };

  const handleEdit = (item: any) => {
    setEditingItem({ ...item });
  };

  const handleUpdate = async () => {
    if (!editingItem || !editingItem.name) {
      toast.error('Name is required');
      return;
    }

    try {
      const res = await apiClient.updateJobParameter(activeTab, editingItem.id, editingItem);
      const updatedItem = { ...res.item, id: res.item._id || res.item.id };

      const updated = {
        ...parameters,
        [activeTab]: (parameters[activeTab as keyof typeof parameters] as any).map((item: any) =>
          item.id === updatedItem.id ? updatedItem : item
        )
      } as any;
      setParameters(updated);
      setJobParameters(updated);

      setEditingItem(null);
      toast.success(`${currentConfig.title} item updated successfully!`);
    } catch (e: any) {
      toast.error(e.message || 'Failed to update item');
    }
  };

  const handleDelete = async (id: string) => {
    const itemToDelete = currentData.find((item: any) => item.id === id);
    const confirmed = await confirm({
      title: `Delete ${activeTab.slice(0, -1).replace(/([A-Z])/g, ' $1').toLowerCase()}`,
      description: `Are you sure you want to delete this ${activeTab.slice(0, -1).replace(/([A-Z])/g, ' $1').toLowerCase()}? This action cannot be undone.`,
      type: 'danger',
      itemName: itemToDelete?.name,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;
    try {
      await apiClient.deleteJobParameter(activeTab, id);
      const updated = {
        ...parameters,
        [activeTab]: (parameters[activeTab as keyof typeof parameters] as any).filter((item: any) => item.id !== id)
      } as any;
      setParameters(updated);
      setJobParameters(updated);
      toast.success(`${currentConfig.title} item deleted successfully!`);
    } catch (e: any) {
      const msg = e?.message || 'Failed to delete item';
      toast.error(msg);
    }
  };

  const toggleStatus = async (id: string) => {
    const list = parameters[activeTab as keyof typeof parameters] as any[];
    const item = list.find(i => i.id === id);
    if (!item) return;

    const newStatus = item.status === 'active' ? 'inactive' : 'active';
    try {
      await apiClient.updateJobParameter(activeTab, id, { status: newStatus });
      const updatedList = list.map((i: any) =>
        i.id === id ? { ...i, status: newStatus } : i
      );

      const updated = {
        ...parameters,
        [activeTab]: updatedList
      } as any;
      setParameters(updated);
      setJobParameters(updated);
      toast.success('Status updated successfully!');
    } catch (e: any) {
      toast.error(e.message || 'Failed to update status');
    }
  };

  const renderTableRow = (item: any) => {
    switch (activeTab) {
      case 'skills':
        return (
          <tr key={item.id} className="border-b hover:bg-gray-50">
            <td className="py-2 px-2 text-sm font-medium">{item.name}</td>
            <td className="py-3 px-4">
              <Badge variant="outline">{item.category}</Badge>
            </td>
            <td className="py-2 px-2 text-xs">{item.usage} jobs</td>
            <td className="py-2 px-2">
              <Badge className={item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                {item.status}
              </Badge>
            </td>
            <td className="py-2 px-2">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleStatus(item.id)}
                  className={item.status === 'active' ? 'text-red-600' : 'text-green-600'}
                >
                  {item.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(item.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </td>
          </tr>
        );

      case 'employeeTypes':
      case 'jobLevels':
        return (
          <tr key={item.id} className="border-b hover:bg-gray-50">
            <td className="py-2 px-2 text-sm font-medium">{item.name}</td>
            <td className="py-2 px-2 text-xs text-gray-600">{item.description}</td>
            <td className="py-2 px-2 text-xs">{item.usage} jobs</td>
            <td className="py-2 px-2">
              <Badge className={item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                {item.status}
              </Badge>
            </td>
            <td className="py-2 px-2">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleStatus(item.id)}
                  className={item.status === 'active' ? 'text-red-600' : 'text-green-600'}
                >
                  {item.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(item.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </td>
          </tr>
        );

      case 'languages':
        return (
          <tr key={item.id} className="border-b hover:bg-gray-50">
            <td className="py-2 px-2 text-sm font-medium">{item.name}</td>
            <td className="py-3 px-4">
              <Badge variant="outline">{item.level}</Badge>
            </td>
            <td className="py-2 px-2 text-xs">{item.usage} jobs</td>
            <td className="py-2 px-2">
              <Badge className={item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                {item.status}
              </Badge>
            </td>
            <td className="py-2 px-2">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleStatus(item.id)}
                  className={item.status === 'active' ? 'text-red-600' : 'text-green-600'}
                >
                  {item.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(item.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </td>
          </tr>
        );

      case 'experience':
        return (
          <tr key={item.id} className="border-b hover:bg-gray-50">
            <td className="py-2 px-2 text-sm font-medium">{item.name}</td>
            <td className="py-3 px-4">
              <Badge variant="outline">{item.range}</Badge>
            </td>
            <td className="py-2 px-2 text-xs">{item.usage} jobs</td>
            <td className="py-2 px-2">
              <Badge className={item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                {item.status}
              </Badge>
            </td>
            <td className="py-2 px-2">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleStatus(item.id)}
                  className={item.status === 'active' ? 'text-red-600' : 'text-green-600'}
                >
                  {item.status === 'active' ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(item.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </td>
          </tr>
        );

      default:
        return null;
    }
  };

  const renderAddForm = () => (
    <div className="space-y-3 text-xs">
      <div className="space-y-1.5">
        <Label htmlFor="itemName" className="text-xs">Name</Label>
        <Input
          id="itemName"
          value={newItem.name}
          onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
          placeholder={`Enter ${activeTab.slice(0, -1)} name`}
          className="h-8 text-xs"
        />
      </div>

      {currentConfig.fields.includes('description') && (
        <div className="space-y-1.5">
          <Label htmlFor="itemDescription" className="text-xs">Description</Label>
          <Textarea
            id="itemDescription"
            value={newItem.description}
            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
            placeholder="Enter description"
            rows={3}
            className="text-xs"
          />
        </div>
      )}

      {currentConfig.fields.includes('category') && (
        <div className="space-y-1.5">
          <Label htmlFor="itemCategory" className="text-xs">Category</Label>
          <Input
            id="itemCategory"
            value={newItem.category}
            onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
            placeholder="Enter category"
            className="h-8 text-xs"
          />
        </div>
      )}

      {currentConfig.fields.includes('level') && (
        <div className="space-y-1.5">
          <Label htmlFor="itemLevel" className="text-xs">Level</Label>
          <select
            id="itemLevel"
            value={newItem.level}
            onChange={(e) => setNewItem({ ...newItem, level: e.target.value })}
            className="w-full h-8 text-xs px-2 border rounded"
          >
            <option value="">Select level</option>
            <option value="Required">Required</option>
            <option value="Preferred">Preferred</option>
            <option value="Native">Native</option>
            <option value="Basic">Basic</option>
            <option value="Fluent">Fluent</option>
          </select>
        </div>
      )}

      {currentConfig.fields.includes('range') && (
        <div className="space-y-1.5">
          <Label htmlFor="itemRange" className="text-xs">Range</Label>
          <Input
            id="itemRange"
            value={newItem.range}
            onChange={(e) => setNewItem({ ...newItem, range: e.target.value })}
            placeholder="e.g., 5-7 years"
            className="h-8 text-xs"
          />
        </div>
      )}
    </div>
  );

  const renderEditForm = () => (
    <div className="space-y-3 text-xs">
      <div className="space-y-1.5">
        <Label htmlFor="editItemName" className="text-xs">Name</Label>
        <Input
          id="editItemName"
          value={editingItem?.name || ''}
          onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
          className="h-8 text-xs"
        />
      </div>

      {currentConfig.fields.includes('description') && (
        <div className="space-y-1.5">
          <Label htmlFor="editItemDescription" className="text-xs">Description</Label>
          <Textarea
            id="editItemDescription"
            value={editingItem?.description || ''}
            onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
            rows={3}
            className="text-xs"
          />
        </div>
      )}

      {currentConfig.fields.includes('category') && (
        <div className="space-y-1.5">
          <Label htmlFor="editItemCategory" className="text-xs">Category</Label>
          <Input
            id="editItemCategory"
            value={editingItem?.category || ''}
            onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
            className="h-8 text-xs"
          />
        </div>
      )}

      {currentConfig.fields.includes('level') && (
        <div className="space-y-1.5">
          <Label htmlFor="editItemLevel" className="text-xs">Level</Label>
          <select
            id="editItemLevel"
            value={editingItem?.level || ''}
            onChange={(e) => setEditingItem({ ...editingItem, level: e.target.value })}
            className="w-full h-8 text-xs px-2 border rounded"
          >
            <option value="">Select level</option>
            <option value="Required">Required</option>
            <option value="Preferred">Preferred</option>
            <option value="Native">Native</option>
            <option value="Basic">Basic</option>
            <option value="Fluent">Fluent</option>
          </select>
        </div>
      )}

      {currentConfig.fields.includes('range') && (
        <div className="space-y-1.5">
          <Label htmlFor="editItemRange" className="text-xs">Range</Label>
          <Input
            id="editItemRange"
            value={editingItem?.range || ''}
            onChange={(e) => setEditingItem({ ...editingItem, range: e.target.value })}
            className="h-8 text-xs"
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      {ConfirmDialog}
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <h2 className="text-2xl font-bold">Job Parameter Management</h2>
          <p className="text-gray-600">Manage job-related parameters, skills, and requirements</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
      <div className="flex justify-center w-full mb-8">
        <Menubar className="h-12 bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-full p-1 shadow-lg shadow-gray-200/20 relative w-full">
          {[
            { id: 'skills', label: 'Skills', icon: Code },
            { id: 'employeeTypes', label: 'Employee Types', icon: Users },
            { id: 'languages', label: 'Languages', icon: Globe },
            { id: 'jobLevels', label: 'Job Levels', icon: GraduationCap },
            { id: 'experience', label: 'Experience', icon: Calendar },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <MenubarMenu key={tab.id} className="flex-1">
                <MenubarTrigger
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`
                    relative z-10 flex cursor-pointer items-center justify-center gap-2 rounded-full px-6 py-2 text-sm font-medium transition-all duration-300 w-full
                    ${isActive ? 'text-white' : 'text-gray-500 hover:text-gray-900'}
                    bg-transparent border-none shadow-none focus:bg-transparent data-[state=open]:bg-transparent
                  `}
                >
                  <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  
                  {isActive && (
                    <motion.div
                      layoutId="activeJobParamTabIndicator"
                      className="absolute inset-0 bg-[#e37300] rounded-full shadow-sm"
                      style={{ zIndex: -1 }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </MenubarTrigger>
              </MenubarMenu>
            );
          })}
        </Menubar>
      </div>

        {Object.keys(parameterConfigs).map(tabKey => (
          <TabsContent key={tabKey} value={tabKey} className="space-y-3">
            {/* Tab Header */}
            <Card>
              <CardContent className="pt-2">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <div className="flex items-center gap-3">
                    {React.createElement(parameterConfigs[tabKey as keyof typeof parameterConfigs].icon, { className: "h-8 w-8 text-blue-600" })}
                    <div>
                      <h3 className="text-lg font-semibold">{currentConfig.title}</h3>
                      <p className="text-sm text-gray-600">{currentConfig.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Add {tabKey.slice(0, -1)}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add {currentConfig.title.slice(0, -1)}</DialogTitle>
                          <DialogDescription>
                            Create a new {tabKey.slice(0, -1)} entry.
                          </DialogDescription>
                        </DialogHeader>
                        {renderAddForm()}
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleAdd}>Add</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <Card>
                <CardContent className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Items</p>
                      <p className="text-2xl font-bold text-blue-600">{currentData.length}</p>
                    </div>
                    <Settings className="h-8 w-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Active</p>
                      <p className="text-2xl font-bold text-green-600">
                        {currentData.filter(item => item.status === 'active').length}
                      </p>
                    </div>
                    <Settings className="h-8 w-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Most Used</p>
                      <p className="text-lg font-bold text-purple-600">
                        {currentData.length
                          ? currentData.reduce((max, item) => item.usage > (max?.usage || 0) ? item : max, currentData[0])?.name
                          : 'N/A'}
                      </p>
                    </div>
                    <Settings className="h-8 w-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Usage</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {currentData.reduce((sum, item) => sum + (Number(item.usage) || 0), 0)}
                      </p>
                    </div>
                    <Settings className="h-8 w-8 text-orange-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Data Table */}
            <Card>
              <CardHeader>
                <CardTitle>{currentConfig.title} ({filteredData.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        {currentConfig.columns.map(column => (
                          <th key={column} className="text-left py-2 px-2 text-xs">{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map(item => renderTableRow(item))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span>Show</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      className="px-2 py-1 bg-white border rounded"
                    >
                      {[10, 25, 50, 100].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <span>entries</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Previous</Button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>Next</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent className="p-4">
          <DialogHeader>
            <DialogTitle>Edit {currentConfig.title.slice(0, -1)}</DialogTitle>
            <DialogDescription>
              Update the {activeTab.slice(0, -1)} information.
            </DialogDescription>
          </DialogHeader>
          {renderEditForm()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingItem(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
